import { createContext, type ReactNode, useContext } from "react";

export type ReviewSection =
  | "week"
  | "insights"
  | "changes"
  | "progress"
  | "timeline";

export interface ReviewCause {
  key: string;
  label: string;
  description?: string | null;
}

export interface ReviewNextStep {
  key: string;
  label: string;
}

/**
 * The only spec a page reads. The API fills every default; the app never
 * fills a declaration in itself.
 */
export interface ReviewSpec {
  work: string | null;
  headline: string[];
  metric: {
    label: string;
    meaning:
      | "gross_profit"
      | "net_profit"
      | "commission"
      | "labor"
      | "hours"
      | "other";
    target: number | null;
    /**
     * Which way the rate should move, as the API resolved it: the metric's
     * declaration, else the result column's, else lower for a labor share
     * and higher for anything else. Missing from an older payload.
     */
    better?: "higher" | "lower";
  };
  severity: { of: "result" | "total"; high: number; medium: number };
  sections: ReviewSection[];
  /** The causes a finding's guess can name, base and declared, resolved. */
  causes?: ReviewCause[];
  next_steps: ReviewNextStep[];
  labels: { review: string; reviewed: string };
  /**
   * The rule ids in the order the owner put them (Settings → Review rules),
   * which the flagged groups follow; empty, or missing from an older
   * payload, for the automatic order (what each costs).
   */
  rule_order?: string[];
}

export const ALL_SECTIONS: ReviewSection[] = [
  "week",
  "insights",
  "changes",
  "progress",
  "timeline",
];

/**
 * What a page reads before the API's spec arrives, or where there is none
 * (a draft's sample has no template overview): neutral words, nothing
 * declared. Only the context's default; never merged with a declaration.
 */
export const EMPTY_REVIEW: ReviewSpec = {
  work: null,
  headline: [],
  metric: { label: "Rate", meaning: "other", target: null },
  severity: { of: "result", high: 0.01, medium: 0.0025 },
  sections: ALL_SECTIONS,
  causes: [],
  next_steps: [],
  labels: { review: "To review", reviewed: "Mark reviewed" },
  rule_order: [],
};

/**
 * Whether result ÷ total is a profit margin, so a shortfall in money below
 * the average margin means something. For a commission or hours report the
 * page shows the rate under its own name and no money shortfall.
 */
export const isProfit = (spec: ReviewSpec) =>
  spec.metric.meaning === "gross_profit" ||
  spec.metric.meaning === "net_profit";

/**
 * Whether result ÷ total is a share of money (a margin, a commission or
 * labor rate), so a change in it reads in points. Hours over hours, or a
 * figure the script calls `other`, is left as the figures themselves.
 */
export const isMoneyRate = (spec: ReviewSpec) =>
  isProfit(spec) ||
  spec.metric.meaning === "commission" ||
  spec.metric.meaning === "labor";

const Context = createContext<ReviewSpec>(EMPTY_REVIEW);

/** The neutral spec until `overview.review` arrives. */
export const ReviewSpecProvider = ({
  spec,
  children,
}: {
  spec: ReviewSpec | null | undefined;
  children: ReactNode;
}) => (
  <Context.Provider value={spec ?? EMPTY_REVIEW}>{children}</Context.Provider>
);

export const useReviewSpec = () => useContext(Context);
